import { getDateRange } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useFetchCompanyDocs, {
  FilingsSortOption,
  SearchFilingsPayload,
} from '../../../../hooks/documents/useFetchFilings';
import { CompanyPageParams } from '../../../../models/CompanyPageParams';
import styles from './CompanyFilingsTable.module.scss';
import { COMPANY_DOCS_TABLE_CONFIG } from '../CompanyDocumentsTab.logic';
import DocumentsTable from '../DocumentsTable';

const cx = classNames.bind(styles);

const CompanyFilingsTable = (): React.ReactElement => {
  const { issuerId } = useParams() as CompanyPageParams;

  const [fetchDocsPayload, setFetchDocsPayload] = useState<SearchFilingsPayload>({
    skip: 0,
    take: 100,
    source: ['lfi', 'cms'],
    sort: 'date',
    sortDesc: true,
  });
  const { data, isLoading } = useFetchCompanyDocs(issuerId, fetchDocsPayload);

  const [dateRange, setDateRange] = useState();

  const onSort = (sortBy: string) => {
    setFetchDocsPayload((prevPayload) => {
      if (prevPayload.sort === sortBy) {
        return { ...prevPayload, sortDesc: !prevPayload.sortDesc };
      }

      return { ...prevPayload, sortDesc: true, sort: sortBy as FilingsSortOption };
    });
  };

  const handleFilter = useCallback(
    (key: string, value: any) => {
      const nextObject: any = {};

      switch (key) {
        case 'date':
          setDateRange(value);

          if (value.relativeRange) {
            const { startDate, endDate } = getDateRange(value.relativeRange);

            nextObject.dateFrom = startDate ? startDate.toDateString() : null;
            nextObject.dateTo = endDate ? endDate.toDateString() : null;
          } else if (value.startDate && value.endDate) {
            nextObject.dateFrom = value.startDate ? value.startDate.toDateString() : null;
            nextObject.dateTo = value.endDate ? value.endDate.toDateString() : null;
          } else if (value.relativeRange === null) {
            nextObject.dateFrom = null;
            nextObject.dateTo = null;
          } else {
            return;
          }
          break;
        case 'type':
          nextObject.types = value;
          break;
        case 'uploadedFile.originalFilename':
          nextObject.fileName = value;
          break;
        default:
          nextObject[key] = value;
          break;
      }

      setFetchDocsPayload((prevPayload) => ({
        ...prevPayload,
        ...nextObject,
      }));
    },
    [setDateRange, setFetchDocsPayload]
  );

  const filters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { skip, take, source, sort, sortDesc, fileName, dateFrom, dateTo, types, ...filters } = fetchDocsPayload;

    return {
      'uploadedFile.originalFilename': fileName ?? '',
      date: dateRange,
      type: types,
      ...filters,
    };
  }, [dateRange, fetchDocsPayload]);

  return (
    <div>
      <div className={cx('results')}>
        Results <span>{data?.totalResults.toLocaleString('en-US')}</span>
      </div>
      <DocumentsTable
        config={COMPANY_DOCS_TABLE_CONFIG}
        data={data}
        isLoading={isLoading}
        onSort={onSort}
        sortBy={fetchDocsPayload.sort}
        sortDesc={fetchDocsPayload.sortDesc}
        onFilter={handleFilter}
        filters={filters}
        dataTestId="company-docs-table"
        noCaret
      />
    </div>
  );
};

export default CompanyFilingsTable;
